import {RawMaterialsClassificationInputNames, MaterialType} from '@domain/materials'
import {dataTestId} from '@hconnect/uikit'
import {Box} from '@mui/material'
import {useFormContext} from 'react-hook-form'

import {useMaterialsClassificationMetadata} from '../../../hooks'
import {MaterialFormDefaultValues} from '../../materials-details/ExistingMaterialDetails/hooks'
import {MaterialsGrid} from '../components/MaterialsGrid'
import {MaterialsGridItem} from '../components/MaterialsGridItem'

import {ClassificationAutocomplete} from './inputs/ClassificationAutocomplete'
import {ClassificationCheckBox} from './inputs/ClassificationCheckbox'
import {ClassificationSegmentedButton} from './inputs/ClassificationSegmentedButton'
import {ClassificationTextField} from './inputs/ClassificationTextField'

interface RawMaterialsClassificationProps {
  isDisabled: boolean
}

export const RawMaterialsClassification: React.FC<RawMaterialsClassificationProps> = ({
  isDisabled
}) => {
  const {data: rawMaterialsClassificationMetadata} = useMaterialsClassificationMetadata(
    MaterialType.RawMaterials
  )

  const {setValue, getValues, clearErrors} = useFormContext<MaterialFormDefaultValues>()
  if (!rawMaterialsClassificationMetadata) {
    throw new Error(
      'BUG: classification metadata should be loaded before rendering classification tab'
    )
  }

  return (
    <MaterialsGrid {...dataTestId('RawMaterials_classification_form')}>
      <MaterialsGridItem>
        <ClassificationSegmentedButton
          materialType={MaterialType.RawMaterials}
          inputName={RawMaterialsClassificationInputNames.RawMaterialType}
          metadata={rawMaterialsClassificationMetadata}
          isDisabled={isDisabled}
          onChangeCb={() => {
            setValue('classification', {
              ...getValues('classification'),
              [RawMaterialsClassificationInputNames.RawMaterialCategory]: null,
              [RawMaterialsClassificationInputNames.RawMaterialSubCategory]: null,
              [RawMaterialsClassificationInputNames.FormSize]: null,
              [RawMaterialsClassificationInputNames.ProcessingDegree]: null,
              [RawMaterialsClassificationInputNames.UsedForCement]: 'No',
              [RawMaterialsClassificationInputNames.UsedForClinker]: 'No'
            })
            clearErrors([
              `classification.${RawMaterialsClassificationInputNames.RawMaterialCategory}`,
              `classification.${RawMaterialsClassificationInputNames.RawMaterialSubCategory}`,
              `classification.${RawMaterialsClassificationInputNames.FormSize}`,
              `classification.${RawMaterialsClassificationInputNames.ProcessingDegree}`,
              `classification.${RawMaterialsClassificationInputNames.UsedForCement}`,
              `classification.${RawMaterialsClassificationInputNames.UsedForClinker}`
            ])
          }}
        />
        <Box sx={{display: 'flex', flexDirection: 'column'}}>
          <ClassificationCheckBox
            materialType={MaterialType.RawMaterials}
            inputName={RawMaterialsClassificationInputNames.UsedForClinker}
            customValues={{trueValue: 'Yes', falseValue: 'No'}}
            metadata={rawMaterialsClassificationMetadata}
            isDisabled={isDisabled}
          />
          <ClassificationCheckBox
            materialType={MaterialType.RawMaterials}
            inputName={RawMaterialsClassificationInputNames.UsedForCement}
            customValues={{trueValue: 'Yes', falseValue: 'No'}}
            metadata={rawMaterialsClassificationMetadata}
            isDisabled={isDisabled}
          />
        </Box>
      </MaterialsGridItem>
      <MaterialsGridItem>
        <ClassificationAutocomplete
          materialType={MaterialType.RawMaterials}
          inputName={RawMaterialsClassificationInputNames.RawMaterialCategory}
          metadata={rawMaterialsClassificationMetadata}
          onChangeCb={() => {
            const currentClassification = getValues('classification')
            setValue('classification', {
              ...currentClassification,
              [RawMaterialsClassificationInputNames.RawMaterialSubCategory]: null,
              [RawMaterialsClassificationInputNames.FormSize]: null,
              [RawMaterialsClassificationInputNames.ProcessingDegree]: null,
              [RawMaterialsClassificationInputNames.UsedForCement]:
                currentClassification?.[RawMaterialsClassificationInputNames.UsedForCement] || 'No',
              [RawMaterialsClassificationInputNames.UsedForClinker]:
                currentClassification?.[RawMaterialsClassificationInputNames.UsedForClinker] || 'No'
            })
            clearErrors([
              `classification.${RawMaterialsClassificationInputNames.RawMaterialSubCategory}`,
              `classification.${RawMaterialsClassificationInputNames.FormSize}`,
              `classification.${RawMaterialsClassificationInputNames.ProcessingDegree}`,
              `classification.${RawMaterialsClassificationInputNames.UsedForCement}`,
              `classification.${RawMaterialsClassificationInputNames.UsedForClinker}`
            ])
          }}
          isDisabled={isDisabled}
        />
      </MaterialsGridItem>
      <MaterialsGridItem>
        <ClassificationAutocomplete
          materialType={MaterialType.RawMaterials}
          inputName={RawMaterialsClassificationInputNames.RawMaterialSubCategory}
          metadata={rawMaterialsClassificationMetadata}
          isDisabled={isDisabled}
        />
      </MaterialsGridItem>
      <MaterialsGridItem>
        <ClassificationAutocomplete
          materialType={MaterialType.RawMaterials}
          inputName={RawMaterialsClassificationInputNames.FormSize}
          metadata={rawMaterialsClassificationMetadata}
          isDisabled={isDisabled}
        />
      </MaterialsGridItem>
      <MaterialsGridItem>
        <ClassificationAutocomplete
          materialType={MaterialType.RawMaterials}
          inputName={RawMaterialsClassificationInputNames.ProcessingDegree}
          metadata={rawMaterialsClassificationMetadata}
          isDisabled={isDisabled}
        />
      </MaterialsGridItem>
      <MaterialsGridItem>
        <ClassificationTextField
          materialType={MaterialType.RawMaterials}
          inputName={RawMaterialsClassificationInputNames.LegacyId}
          metadata={rawMaterialsClassificationMetadata}
          isDisabled={isDisabled}
        />
      </MaterialsGridItem>
    </MaterialsGrid>
  )
}
